import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { API_CONFIG } from '../../config/api';
import { Product, CreateProductRequest, UpdateProductRequest } from '../../types/Product';
import { ApiResponse } from '../../types/ApiResponse';
import { readApiResponse } from '../../lib/utils/apiResponse';

const API_BASE_URL = API_CONFIG.API_URL;

interface ProductsState {
  products: Product[];
  currentProduct: Product | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const initialState: ProductsState = {
  products: [],
  currentProduct: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },
};

// Fetch all products
export const fetchProducts = createAsyncThunk(
  'products/fetchAll',
  async (params?: { pageNumber?: number; pageSize?: number; searchTerm?: string; sortBy?: string; categoryId?: string; subcategoryId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.subcategoryId) queryParams.append('subcategoryId', params.subcategoryId);
    
    const url = `${API_BASE_URL}/product${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data: ApiResponse = await readApiResponse(response);
    return data.result;
  }
);

// Fetch trashed products
export const fetchTrashedProducts = createAsyncThunk(
  'products/fetchTrashed',
  async (params?: { pageNumber?: number; pageSize?: number; searchTerm?: string; sortBy?: string; categoryId?: string; subcategoryId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.subcategoryId) queryParams.append('subcategoryId', params.subcategoryId);
    
    const url = `${API_BASE_URL}/product/trashed${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      headers: { 
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    const data: ApiResponse = await readApiResponse(response);
    return data.result;
  }
);

// Fetch featured products (paginated for admin)
export const fetchFeaturedProductsPaged = createAsyncThunk(
  'products/fetchFeaturedPaged',
  async (params?: { pageNumber?: number; pageSize?: number; searchTerm?: string; sortBy?: string; categoryId?: string; subcategoryId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.subcategoryId) queryParams.append('subcategoryId', params.subcategoryId);
    
    const url = `${API_BASE_URL}/product/featured-paged${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      headers: { 
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    const data: ApiResponse = await readApiResponse(response);
    return data.result;
  }
);

// Fetch best seller products (paginated for admin)
export const fetchBestSellerProductsPaged = createAsyncThunk(
  'products/fetchBestSellersPaged',
  async (params?: { pageNumber?: number; pageSize?: number; searchTerm?: string; sortBy?: string; categoryId?: string; subcategoryId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.subcategoryId) queryParams.append('subcategoryId', params.subcategoryId);
    
    const url = `${API_BASE_URL}/product/bestsellers-paged${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      headers: { 
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    const data: ApiResponse = await readApiResponse(response);
    return data.result;
  }
);

// Fetch products by category
export const fetchProductsByCategory = createAsyncThunk(
  'products/fetchByCategory',
  async (params: { categoryId: string; searchTerm?: string; sortBy?: string }) => {
    const { categoryId, ...rest } = params;
    const queryParams = new URLSearchParams();
    if (rest.searchTerm) queryParams.append('searchTerm', rest.searchTerm);
    if (rest.sortBy) queryParams.append('sortBy', rest.sortBy);
    
    const url = `${API_BASE_URL}/product/category/${categoryId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse = await readApiResponse(response);
    return data.result as Product[];
  }
);

// Fetch products by subcategory
export const fetchProductsBySubcategory = createAsyncThunk(
  'products/fetchBySubcategory',
  async (params: { subcategoryId: string; searchTerm?: string; sortBy?: string }) => {
    const { subcategoryId, ...rest } = params;
    const queryParams = new URLSearchParams();
    if (rest.searchTerm) queryParams.append('searchTerm', rest.searchTerm);
    if (rest.sortBy) queryParams.append('sortBy', rest.sortBy);
    
    const url = `${API_BASE_URL}/product/subcategory/${subcategoryId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse = await readApiResponse(response);
    return data.result as Product[];
  }
);

// Fetch featured products
export const fetchFeaturedProducts = createAsyncThunk(
  'products/fetchFeatured',
  async (limit: number = 10) => {
    const url = `${API_BASE_URL}/product/featured?limit=${limit}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse = await readApiResponse(response);
    return data.result as Product[];
  }
);

// Fetch best sellers
export const fetchBestSellers = createAsyncThunk(
  'products/fetchBestSellers',
  async (limit: number = 10) => {
    const url = `${API_BASE_URL}/product/bestsellers?limit=${limit}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse = await readApiResponse(response);
    return data.result as Product[];
  }
);

// Fetch single product
export const fetchProductById = createAsyncThunk(
  'products/fetchById',
  async (id: string) => {
    const url = `${API_BASE_URL}/product/${id}`;
    const response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    const data: ApiResponse = await readApiResponse(response);
    return data.result as Product;
  }
);

// Create product
export const createProduct = createAsyncThunk(
  'products/create',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/product`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData, // Send FormData directly (no Content-Type header needed)
      });
      
      const data: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !data.isSuccess) {
        const errorMessage = data.errorMessages?.length > 0 
          ? data.errorMessages.join(', ') 
          : `HTTP error! status: ${response.status}`;
        console.error('API Error:', errorMessage);
        console.error('Full error data:', data);
        return rejectWithValue(errorMessage);
      }
      
      return data.result as Product;
    } catch (error: any) {
      console.error('Network error:', error);
      return rejectWithValue(error.message || 'Failed to create product');
    }
  }
);

// Update product
export const updateProduct = createAsyncThunk(
  'products/update',
  async ({ id, data }: { id: string; data: UpdateProductRequest }) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/product/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });
    const apiResponse: ApiResponse = await readApiResponse(response);
    return apiResponse.result as Product;
  }
);

// Delete product
export const deleteProduct = createAsyncThunk(
  'products/delete',
  async (id: string) => {
    const token = localStorage.getItem('accessToken');
    await fetch(`${API_BASE_URL}/product/${id}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    return id;
  }
);

// Restore product
export const restoreProduct = createAsyncThunk(
  'products/restore',
  async (id: string) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/product/${id}/restore`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return id;
  }
);

// Toggle featured status
export const toggleFeatured = createAsyncThunk(
  'products/toggleFeatured',
  async (id: string) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/product/${id}/toggle-featured`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await readApiResponse(response);
    return { id, isFeatured: data.result.isFeatured };
  }
);

// Toggle best seller status
export const toggleBestSeller = createAsyncThunk(
  'products/toggleBestSeller',
  async (id: string) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/product/${id}/toggle-bestseller`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await readApiResponse(response);
    return { id, isBestSeller: data.result.isBestSeller };
  }
);

// Update featured order
export const updateFeaturedOrder = createAsyncThunk(
  'products/updateFeaturedOrder',
  async ({ id, order }: { id: string; order: number }) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/product/${id}/featured-order`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(order),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await readApiResponse(response);
    return { id, featuredOrder: data.result.featuredOrder };
  }
);

// Update best seller order
export const updateBestSellerOrder = createAsyncThunk(
  'products/updateBestSellerOrder',
  async ({ id, order }: { id: string; order: number }) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/product/${id}/bestseller-order`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(order),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await readApiResponse(response);
    return { id, bestSellerOrder: data.result.bestSellerOrder };
  }
);

// Toggle out of stock status
export const toggleOutOfStock = createAsyncThunk(
  'products/toggleOutOfStock',
  async (id: string) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/product/${id}/toggle-stock`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await readApiResponse(response);
    return { id, isOutOfStock: data.result.isOutOfStock };
  }
);

// Upload product image (using ProductImageController)
export const uploadProductImage = createAsyncThunk(
  'products/uploadImage',
  async ({ productId, file, isMain, order }: { productId: string; file: File; isMain: boolean; order: number }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('accessToken');

      const response = await fetch(
        `${API_BASE_URL}/product/${productId}/images?isMain=${isMain}&order=${order}`,
        {
          method: 'POST',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        }
      );
      const data: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !data.isSuccess) {
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to upload image');
      }
      
      return { productId, image: data.result };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload image');
    }
  }
);

// Delete product image (using ProductImageController)
export const deleteProductImage = createAsyncThunk(
  'products/deleteImage',
  async ({ productId, imageId }: { productId: string; imageId: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/product/${productId}/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      
      if (!response.ok) {
        const data = await readApiResponse(response);
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to delete image');
      }
      
      return { productId, imageId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete image');
    }
  }
);

// Set main product image (using ProductImageController)
export const setMainProductImage = createAsyncThunk(
  'products/setMainImage',
  async ({ productId, imageId }: { productId: string; imageId: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/product/${productId}/images/${imageId}?isMain=true`,
        {
          method: 'PUT',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      
      if (!response.ok) {
        const data = await readApiResponse(response);
        return rejectWithValue(data.errorMessages?.[0] || 'Failed to set main image');
      }
      
      return { productId, imageId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to set main image');
    }
  }
);

// Update product variant
export const updateProductVariant = createAsyncThunk(
  'products/updateVariant',
  async ({ productId, variantId, data }: { productId: string; variantId: string; data: any }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/products/${productId}/variants/${variantId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(data),
        }
      );
      
      const apiResponse: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !apiResponse.isSuccess) {
        const errorMessage = apiResponse.errorMessages?.length > 0 
          ? apiResponse.errorMessages.join(', ') 
          : `HTTP error! status: ${response.status}`;
        return rejectWithValue(errorMessage);
      }
      
      return { productId, variant: apiResponse.result };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update variant');
    }
  }
);

// Add product variant
export const addProductVariant = createAsyncThunk(
  'products/addVariant',
  async ({ productId, data }: { productId: string; data: any }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/products/${productId}/variants`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(data),
        }
      );
      
      const apiResponse: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !apiResponse.isSuccess) {
        const errorMessage = apiResponse.errorMessages?.length > 0 
          ? apiResponse.errorMessages.join(', ') 
          : `HTTP error! status: ${response.status}`;
        return rejectWithValue(errorMessage);
      }
      
      return { productId, variant: apiResponse.result };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add variant');
    }
  }
);

// Delete product variant
export const deleteProductVariant = createAsyncThunk(
  'products/deleteVariant',
  async ({ productId, variantId }: { productId: string; variantId: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/products/${productId}/variants/${variantId}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      
      const apiResponse: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !apiResponse.isSuccess) {
        const errorMessage = apiResponse.errorMessages?.length > 0 
          ? apiResponse.errorMessages.join(', ') 
          : `HTTP error! status: ${response.status}`;
        return rejectWithValue(errorMessage);
      }
      
      return { productId, variantId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete variant');
    }
  }
);

// Set variant as default
export const setVariantAsDefault = createAsyncThunk(
  'products/setVariantAsDefault',
  async ({ productId, variantId }: { productId: string; variantId: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/products/${productId}/variants/${variantId}/set-default`,
        {
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      
      const apiResponse: ApiResponse = await readApiResponse(response);
      
      if (!response.ok || !apiResponse.isSuccess) {
        const errorMessage = apiResponse.errorMessages?.length > 0 
          ? apiResponse.errorMessages.join(', ') 
          : `HTTP error! status: ${response.status}`;
        return rejectWithValue(errorMessage);
      }
      
      return { productId, variantId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to set default variant');
    }
  }
);

// Create option group
export const createOptionGroup = createAsyncThunk(
  'products/createOptionGroup',
  async ({ productId, data }: { productId: string; data: { name: string; order: number; values: { name: string; order: number; priceAdjustment?: number }[] } }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/products/${productId}/option-groups`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(data),
        }
      );
      const apiResponse: ApiResponse = await readApiResponse(response);
      if (!response.ok || !apiResponse.isSuccess) {
        return rejectWithValue(apiResponse.errorMessages?.join(', ') || 'Failed to create option group');
      }
      return { productId, optionGroup: apiResponse.result };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create option group');
    }
  }
);

// Delete option group
export const deleteOptionGroup = createAsyncThunk(
  'products/deleteOptionGroup',
  async ({ productId, groupId }: { productId: string; groupId: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/products/${productId}/option-groups/${groupId}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      const apiResponse: ApiResponse = await readApiResponse(response);
      if (!response.ok || !apiResponse.isSuccess) {
        return rejectWithValue(apiResponse.errorMessages?.join(', ') || 'Failed to delete option group');
      }
      return { productId, groupId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete option group');
    }
  }
);

// Set variant option values
export const setVariantOptionValues = createAsyncThunk(
  'products/setVariantOptionValues',
  async ({ productId, variantId, optionValueIds }: { productId: string; variantId: string; optionValueIds: string[] }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/products/${productId}/variants/${variantId}/option-values`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ optionValueIds }),
        }
      );
      const apiResponse: ApiResponse = await readApiResponse(response);
      if (!response.ok || !apiResponse.isSuccess) {
        return rejectWithValue(apiResponse.errorMessages?.join(', ') || 'Failed to set variant option values');
      }
      return { productId, variant: apiResponse.result };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to set variant option values');
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.products = action.payload?.Data || action.payload?.data || [];
        if (action.payload?.Pagination || action.payload?.pagination) {
          state.pagination = action.payload.Pagination || action.payload.pagination;
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })

      // Fetch trashed products
      .addCase(fetchTrashedProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrashedProducts.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.products = action.payload?.Data || action.payload?.data || [];
        if (action.payload?.Pagination || action.payload?.pagination) {
          state.pagination = action.payload.Pagination || action.payload.pagination;
        }
      })
      .addCase(fetchTrashedProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch trashed products';
      })

      // Fetch featured products (paginated)
      .addCase(fetchFeaturedProductsPaged.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedProductsPaged.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.products = action.payload?.Data || action.payload?.data || [];
        if (action.payload?.Pagination || action.payload?.pagination) {
          state.pagination = action.payload.Pagination || action.payload.pagination;
        }
      })
      .addCase(fetchFeaturedProductsPaged.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch featured products';
      })

      // Fetch best seller products (paginated)
      .addCase(fetchBestSellerProductsPaged.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBestSellerProductsPaged.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.products = action.payload?.Data || action.payload?.data || [];
        if (action.payload?.Pagination || action.payload?.pagination) {
          state.pagination = action.payload.Pagination || action.payload.pagination;
        }
      })
      .addCase(fetchBestSellerProductsPaged.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch best seller products';
      })

      // Fetch by category
      .addCase(fetchProductsByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsByCategory.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProductsByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })

      // Fetch by subcategory
      .addCase(fetchProductsBySubcategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsBySubcategory.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProductsBySubcategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })

      // Fetch featured
      .addCase(fetchFeaturedProducts.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.products = action.payload;
      })

      // Fetch best sellers
      .addCase(fetchBestSellers.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.products = action.payload;
      })

      // Fetch by ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action: PayloadAction<Product>) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch product';
      })

      // Create product
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action: PayloadAction<Product>) => {
        state.loading = false;
        state.products.push(action.payload);
        state.currentProduct = action.payload;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create product';
      })

      // Update product
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action: PayloadAction<Product>) => {
        state.loading = false;
        const index = state.products.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update product';
      })

      // Delete product
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.products = state.products.filter((p) => p.id !== action.payload);
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct = null;
        }
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete product';
      })

      // Restore product
      .addCase(restoreProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreProduct.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.products = state.products.filter((p) => p.id !== action.payload);
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct = null;
        }
      })
      .addCase(restoreProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to restore product';
      })

      // Toggle featured
      .addCase(toggleFeatured.fulfilled, (state, action: PayloadAction<{ id: string; isFeatured: boolean }>) => {
        const product = state.products.find(p => p.id === action.payload.id);
        if (product) {
          product.isFeatured = action.payload.isFeatured;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.isFeatured = action.payload.isFeatured;
        }
      })
      .addCase(toggleFeatured.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to toggle featured status';
      })

      // Toggle best seller
      .addCase(toggleBestSeller.fulfilled, (state, action: PayloadAction<{ id: string; isBestSeller: boolean }>) => {
        const product = state.products.find(p => p.id === action.payload.id);
        if (product) {
          product.isBestSeller = action.payload.isBestSeller;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.isBestSeller = action.payload.isBestSeller;
        }
      })
      .addCase(toggleBestSeller.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to toggle best seller status';
      })

      // Toggle out of stock
      .addCase(toggleOutOfStock.fulfilled, (state, action: PayloadAction<{ id: string; isOutOfStock: boolean }>) => {
        const product = state.products.find(p => p.id === action.payload.id);
        if (product) {
          product.isOutOfStock = action.payload.isOutOfStock;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.isOutOfStock = action.payload.isOutOfStock;
        }
      })
      .addCase(toggleOutOfStock.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to toggle stock status';
      })

      // Upload product image
      .addCase(uploadProductImage.fulfilled, (state, action: PayloadAction<any>) => {
        const { productId, image } = action.payload;
        
        // Update current product if it matches
        if (state.currentProduct && state.currentProduct.id === productId) {
          state.currentProduct.images?.push(image);
        }
        
        // Update product in products array
        const productIndex = state.products.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
          state.products[productIndex].images?.push(image);
        }
      })
      .addCase(uploadProductImage.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to upload image';
      })

      // Delete product image
      .addCase(deleteProductImage.fulfilled, (state, action: PayloadAction<any>) => {
        const { productId, imageId } = action.payload;
        
        // Update current product if it matches
        if (state.currentProduct && state.currentProduct.id === productId) {
          state.currentProduct.images = state.currentProduct.images?.filter(img => img.id !== imageId);
        }
        
        // Update product in products array
        const productIndex = state.products.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
          state.products[productIndex].images = state.products[productIndex].images?.filter(img => img.id !== imageId);
        }
      })
      .addCase(deleteProductImage.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete image';
      })

      // Set main product image
      .addCase(setMainProductImage.fulfilled, (state, action: PayloadAction<any>) => {
        const { productId, imageId } = action.payload;
        
        // Update current product if it matches
        if (state.currentProduct && state.currentProduct.id === productId) {
          state.currentProduct.images?.forEach(img => {
            img.isMain = img.id === imageId;
          });
        }
        
        // Update product in products array
        const productIndex = state.products.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
          state.products[productIndex].images?.forEach(img => {
            img.isMain = img.id === imageId;
          });
        }
      })
      .addCase(setMainProductImage.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to set main image';
      })

      // Update product variant
      .addCase(updateProductVariant.fulfilled, (state, action: PayloadAction<any>) => {
        const { productId, variant } = action.payload;
        
        // Update current product if it matches
        if (state.currentProduct && state.currentProduct.id === productId) {
          const variantIndex = state.currentProduct.variants.findIndex(v => v.id === variant.id);
          if (variantIndex !== -1) {
            state.currentProduct.variants[variantIndex] = variant;
          }
        }
        
        // Update product in products array
        const productIndex = state.products.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
          const variantIndex = state.products[productIndex].variants.findIndex(v => v.id === variant.id);
          if (variantIndex !== -1) {
            state.products[productIndex].variants[variantIndex] = variant;
          }
        }
      })
      .addCase(updateProductVariant.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update variant';
      })

      // Add product variant
      .addCase(addProductVariant.fulfilled, (state, action: PayloadAction<any>) => {
        const { productId, variant } = action.payload;
        
        // Update current product if it matches
        if (state.currentProduct && state.currentProduct.id === productId) {
          state.currentProduct.variants.push(variant);
        }
        
        // Update product in products array
        const productIndex = state.products.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
          state.products[productIndex].variants.push(variant);
        }
      })
      .addCase(addProductVariant.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add variant';
      })

      // Delete product variant
      .addCase(deleteProductVariant.fulfilled, (state, action: PayloadAction<any>) => {
        const { productId, variantId } = action.payload;
        
        // Update current product if it matches
        if (state.currentProduct && state.currentProduct.id === productId) {
          state.currentProduct.variants = state.currentProduct.variants.filter(v => v.id !== variantId);
        }
        
        // Update product in products array
        const productIndex = state.products.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
          state.products[productIndex].variants = state.products[productIndex].variants.filter(v => v.id !== variantId);
        }
      })
      .addCase(deleteProductVariant.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete variant';
      })

      // Set variant as default
      .addCase(setVariantAsDefault.fulfilled, (state, action: PayloadAction<any>) => {
        const { productId, variantId } = action.payload;
        
        // Update current product if it matches
        if (state.currentProduct && state.currentProduct.id === productId) {
          state.currentProduct.variants.forEach(v => {
            v.isDefault = v.id === variantId;
          });
        }
        
        // Update product in products array
        const productIndex = state.products.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
          state.products[productIndex].variants.forEach(v => {
            v.isDefault = v.id === variantId;
          });
        }
      })
      .addCase(setVariantAsDefault.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to set default variant';
      })

      // Update featured order
      .addCase(updateFeaturedOrder.fulfilled, (state, action: PayloadAction<{ id: string; featuredOrder: number }>) => {
        const product = state.products.find(p => p.id === action.payload.id);
        if (product) {
          product.featuredOrder = action.payload.featuredOrder;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.featuredOrder = action.payload.featuredOrder;
        }
      })
      .addCase(updateFeaturedOrder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update featured order';
      })

      // Update best seller order
      .addCase(updateBestSellerOrder.fulfilled, (state, action: PayloadAction<{ id: string; bestSellerOrder: number }>) => {
        const product = state.products.find(p => p.id === action.payload.id);
        if (product) {
          product.bestSellerOrder = action.payload.bestSellerOrder;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.bestSellerOrder = action.payload.bestSellerOrder;
        }
      })
      .addCase(updateBestSellerOrder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update best seller order';
      });
  },
});

export const { clearCurrentProduct, clearError } = productsSlice.actions;
export default productsSlice.reducer;
